import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Building2 } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const NAVY = "#12305C";
const SKY = "#3E7BC4";
const NAME = "Ong Dental Clinic";
const FB = "https://www.facebook.com/OngdentalClinic/";
const FB2 = "https://www.facebook.com/Ongdentalclinicdumaguetecity/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function OngConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Team", "#team"], ["Branches", "#branches"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#141E2E", fontFamily: "'Source Sans 3', sans-serif" }} data-testid="ong-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap'); .ong-serif{font-family:'Merriweather',serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: "#E4E9F1" }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ong-logo" className="flex items-center gap-3">
            <img src="/dental/ong-dental-concept/logo.jpg" alt={NAME} className="h-11 w-11 object-contain" />
            <span className="ong-serif text-lg" style={{ color: NAVY }}>Ong Dental Clinic</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#3A4658] hover:text-[#12305C] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="ong-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: NAVY }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="ong-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — clean navy, no photo (kept for the dedicated team section below) */}
      <section className="py-14 sm:py-20" style={{ background: NAVY }} data-testid="ong-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[1fr_.65fr] gap-10 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: SKY }}>Dumaguete City · Two Locations</span>
            <h1 className="mt-6 ong-serif text-5xl sm:text-6xl text-white leading-[1.1]">Serving Dumaguete Across Two Locations</h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl">General dentistry, restorations and consultations led by Dr. Arnold Ong and the Ong Dental Clinic team.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#visit" data-testid="ong-hero-book" className="px-8 py-3.5 text-sm font-semibold" style={{ background: SKY, color: NAVY }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ong-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/30">Message Us</a>
            </div>
          </motion.div>
          <motion.aside {...fadeUp} className="bg-white p-7"><img src="/dental/ong-dental-concept/logo.jpg" alt="Ong Dental Clinic identity" className="w-full max-h-52 object-contain" /><div className="mt-6 grid grid-cols-2 gap-px bg-[#DDE5F0]"><div className="bg-white p-4"><small className="text-[#5A6478]">Main Clinic</small><strong className="block mt-1" style={{ color: NAVY }}>Sta. Rosa St.</strong></div><div className="bg-white p-4"><small className="text-[#5A6478]">Branch</small><strong className="block mt-1" style={{ color: NAVY }}>Daro</strong></div></div></motion.aside>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl ong-serif leading-relaxed" style={{ color: NAVY }}>
          Ong Dental Clinic has served Dumaguete City for years from two locations, offering general dentistry, restorations and tooth extractions.
        </motion.p>
      </section>

      {/* TEAM — the clinic's own branded team portrait, used as-is */}
      <section id="team" className="py-20" style={{ background: "#EEF3FA" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="ong-serif text-3xl text-center mb-10" style={{ color: NAVY }}>Meet the Team</motion.h2>
          <motion.img {...fadeUp} src="/dental/ong-dental-concept/team.jpg" alt="Dr. Arnold Ong and the Ong Dental Clinic team" className="w-full object-contain shadow-md" />
        </div>
      </section>

      {/* LAB WORK */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
        <motion.img {...fadeUp} src="/dental/ong-dental-concept/lab-crown.jpg" alt="A real dental crown restoration prepared at Ong Dental Clinic" className="w-full rounded-xl shadow-md object-cover h-72" />
        <motion.div {...fadeUp}>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: NAVY }}>Restorations</span>
          <h2 className="mt-3 ong-serif text-3xl" style={{ color: NAVY }}>Careful lab work, every time</h2>
          <p className="mt-4 text-[#3A4658]/80 leading-relaxed">Crowns and restorations are prepared with attention to fit and finish before they ever reach the chair.</p>
        </motion.div>
      </section>

      {/* BRANCHES */}
      <section id="branches" className="py-20" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="ong-serif text-3xl text-white text-center mb-12">Two Locations in Dumaguete</motion.h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { name: "Main Clinic", note: "Sta. Rosa St. corner Cervantes, Dumaguete City", phone: "(035) 522-0288" },
              { name: "Daro Branch", note: "Daro, Dumaguete City", phone: "(035) 422-4159" },
            ].map((b) => (
              <motion.div key={b.name} {...fadeUp} data-testid="ong-branch" className="p-8 border border-white/15">
                <Building2 size={22} style={{ color: SKY }} />
                <h3 className="mt-4 ong-serif text-xl text-white">{b.name}</h3>
                <p className="mt-2 text-white/60">{b.note}</p>
                <p className="mt-2 text-white/60">{b.phone}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="ong-serif text-3xl" style={{ color: NAVY }}>Visit Us</h2>
          <ul className="mt-7 space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: NAVY }} className="mt-0.5" />Sta. Rosa Street corner Cervantes, Dumaguete City</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: NAVY }} className="mt-0.5" /><a href="tel:+63355220288" className="hover:underline">(035) 522-0288</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: NAVY }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ong-facebook-link" className="hover:underline">OngdentalClinic</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: NAVY }} className="mt-0.5" /><a href={FB2} target="_blank" rel="noopener noreferrer" data-testid="ong-facebook2-link" className="hover:underline">Ongdentalclinicdumaguetecity</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px] rounded-xl overflow-hidden shadow-md">
          <iframe title={`${NAME} map`} src="https://www.google.com/maps?q=Dumaguete+City+dental+clinic&output=embed" className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </motion.div>
      </section>

      <footer className="py-10 bg-white border-t" style={{ borderColor: "#E4E9F1" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A6478]">
          <p className="ong-serif text-base" style={{ color: NAVY }}>Ong Dental Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: NAVY }}>Message Ong Dental</a>
      <ConceptCTAPill slug="ong-dental-concept" />
    </div>
  );
}
